import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { mockTemplates } from './mockData';
import type { Template } from './types';
import TemplateDetailModal from './components/TemplateDetailModal';
import TemplateFormModal from './components/TemplateFormModal';
import ConfirmModal from './components/ConfirmModal';

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>(mockTemplates);
  const [searchId, setSearchId] = useState('');
  const [searchName, setSearchName] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('all');
  const [osFilter, setOsFilter] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Template | null>(null);

  const purposes = Array.from(new Set(mockTemplates.map(t => t.purpose)));
  const osList = Array.from(new Set(mockTemplates.map(t => t.os)));

  const filteredTemplates = templates.filter(template => {
    if (searchId && !template.id.toLowerCase().includes(searchId.toLowerCase())) return false;
    if (searchName && !template.name.toLowerCase().includes(searchName.toLowerCase())) return false;
    if (purposeFilter !== 'all' && template.purpose !== purposeFilter) return false;
    if (osFilter !== 'all' && template.os !== osFilter) return false;
    return true;
  });

  const handleToggleStatus = (id: string) => {
    setTemplates(templates.map(template => {
      if (template.id === id) {
        const newStatus = template.status === 'published' ? 'unpublished' : 'published';
        return { ...template, status: newStatus };
      }
      return template;
    }));
  };

  const handleSaveTemplate = (template: Template) => {
    if (editingTemplate) {
      setTemplates(templates.map(t => t.id === template.id ? template : t));
    } else {
      setTemplates([...templates, template]);
    }
    setShowFormModal(false);
    setEditingTemplate(null);
  };

  const handleDeleteTemplate = (template: Template) => {
    setTemplates(templates.filter(t => t.id !== template.id));
    setDeleteConfirm(null);
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setShowFormModal(true);
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setShowFormModal(true);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">云电脑模板</h1>
            <p className="text-sm text-gray-500 mt-1">管理云电脑模板和预装软件配置</p>
          </div>
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line mr-1"></i>
            新建模板
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">模板ID</label>
                <input
                  type="text"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  placeholder="搜索模板ID"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">模板名称</label>
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="搜索模板名称"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">模板用途</label>
                <select
                  value={purposeFilter}
                  onChange={(e) => setPurposeFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">全部用途</option>
                  {purposes.map(purpose => (
                    <option key={purpose} value={purpose}>{purpose}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">操作系统</label>
                <select
                  value={osFilter}
                  onChange={(e) => setOsFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">全部系统</option>
                  {osList.map(os => (
                    <option key={os} value={os}>{os}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {filteredTemplates.map((template) => (
              <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">{template.name}</h3>
                    <p className="text-xs text-gray-500">{template.id}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    template.status === 'published' ? 'bg-green-100 text-green-800' :
                    template.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    'bg-orange-100 text-orange-800'
                  }`}>
                    {template.status === 'published' ? '已上架' : template.status === 'draft' ? '草稿' : '已下架'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-computer-line text-gray-400"></i>
                    <span className="text-gray-600">{template.os}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-apps-line text-gray-400"></i>
                    <span className="text-gray-600">{template.preInstalledSoftware.length} 个预装软件</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-code-box-line text-gray-400"></i>
                    <span className="text-gray-600">版本 {template.currentVersion}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-folder-line text-gray-400"></i>
                    <span className="text-gray-600">{template.purpose}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {template.status !== 'draft' && (
                    <button
                      onClick={() => handleToggleStatus(template.id)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        template.status === 'published'
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {template.status === 'published' ? '下架' : '上架'}
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(template)}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    编辑
                  </button>
                  <button
                    onClick={() => setSelectedTemplate(template)}
                    className="flex-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
                  >
                    详情
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedTemplate && (
        <TemplateDetailModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onEdit={(template) => {
            setSelectedTemplate(null);
            handleEdit(template);
          }}
          onDelete={(template) => {
            setSelectedTemplate(null);
            setDeleteConfirm(template);
          }}
        />
      )}

      {showFormModal && (
        <TemplateFormModal
          template={editingTemplate || undefined}
          onClose={() => {
            setShowFormModal(false);
            setEditingTemplate(null);
          }}
          onSave={handleSaveTemplate}
        />
      )}

      {deleteConfirm && (
        <ConfirmModal
          title="删除模板"
          message={`确定要删除模板"${deleteConfirm.name}"吗？此操作不可恢复。`}
          confirmText="删除"
          cancelText="取消"
          type="danger"
          onConfirm={() => handleDeleteTemplate(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </MainLayout>
  );
}
